import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

/****************
 * PROGRESSIVE DISCLOSURE FOR A WHOLE SECTION. 2026-10-21, engagement redesign.
 *
 * The research: show the simple layer first and let people open depth when
 * they want it. Closed, a section is one slim bar with its name; open, it is
 * exactly what it was, with a small Hide in its corner (no second title).
 * The choice is remembered per section in this browser.
 ****************/
const KEY = (id: string) => `copyfi.fold.${id}`;

function readOpen(id: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(KEY(id));
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

export function FoldSection({ id, title, defaultOpen = false, children, className = "", forceOpen = false }: {
  id: string;
  /** Open now whatever was remembered (a "See all" link pointing here). */
  forceOpen?: boolean;
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(() => forceOpen || readOpen(id, defaultOpen));
  const toggle = (v: boolean) => {
    setOpen(v);
    try { localStorage.setItem(KEY(id), v ? "1" : "0"); } catch { /* this visit only */ }
  };
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => toggle(true)}
        aria-expanded={false}
        className={`w-full glass-card rounded-xl px-5 py-3.5 mb-4 flex items-center justify-between gap-3 text-start hover:border-primary/40 transition-colors ${className}`}
      >
        <span className="text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis">{title}</span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
          {t("fold.show")} <CaretRight size={14} />
        </span>
      </button>
    );
  }
  return (
    <div className={`relative ${className}`}>
      <div className="flex justify-end mb-1">
        <button
          type="button"
          onClick={() => toggle(false)}
          aria-expanded
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground whitespace-nowrap"
        >
          {t("fold.hide", { section: title })} <CaretDown size={14} />
        </button>
      </div>
      {children}
    </div>
  );
}
